import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { FieldError } from "../common/States";

const effective = (rolePerms, granted, revoked) => new Set([...rolePerms.filter((p) => !revoked.includes(p)), ...granted]);

export default function AccessDialog({ target, catalog, onClose, onDone }) {
  const [role, setRole] = useState(target.role);
  const [granted, setGranted] = useState(target.access.granted || []);
  const [revoked, setRevoked] = useState(target.access.revoked || []);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const rolePerms = catalog.roles.find((r) => r.key === role).permissions;
  const perms = role === "super_admin" ? new Set(catalog.catalog.flatMap((m) => m.actions.map((a) => a.key))) : effective(rolePerms, granted, revoked);
  const toggle = (key, on) => {
    if (rolePerms.includes(key)) setRevoked(on ? revoked.filter((k) => k !== key) : [...revoked, key]);
    else setGranted(on ? [...granted, key] : granted.filter((k) => k !== key));
  };
  const changeRole = (r) => { setRole(r); setGranted([]); setRevoked([]); };
  const save = async () => {
    if (reason.trim().length < 5) { setError("Motif requis (5 caractères min.)"); return; }
    setBusy(true);
    try { await api.patch(`/admin/internal/admins/${target.id}`, { role, granted, revoked, reason: reason.trim() }); toast.success("Accès mis à jour"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="admin-access-dialog">
        <DialogHeader><DialogTitle>Accès de {target.name}</DialogTitle></DialogHeader>
        <select className="field-select" value={role} onChange={(e) => changeRole(e.target.value)} data-testid="admin-access-role-select">
          {catalog.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <div className="space-y-3" data-testid="admin-access-matrix">
          {catalog.catalog.map((m) => (
            <div key={m.module} className="rounded-lg border p-3">
              <p className="mb-2 text-sm font-semibold">{m.label}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">{m.actions.map((a) => (
                <label key={a.key} className="flex items-center gap-2 text-xs">
                  <Checkbox checked={perms.has(a.key)} disabled={role === "super_admin"} onCheckedChange={(v) => toggle(a.key, !!v)} data-testid={`admin-access-perm-${a.key.replace(":", "-")}`} />
                  {a.label}{!rolePerms.includes(a.key) && perms.has(a.key) && <span className="text-indigo-600">(ajouté)</span>}{rolePerms.includes(a.key) && !perms.has(a.key) && <span className="text-rose-600">(retiré)</span>}
                </label>))}</div>
            </div>
          ))}
        </div>
        <Textarea rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif du changement (journalisé)" data-testid="admin-access-reason-input" />
        <FieldError>{error}</FieldError>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-access-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-access-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
